import { useRef, useState } from "react";
import { api, isAllowedImage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Image, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export function toAbsoluteUrl(u) {
  if (!u) return u;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
}

export async function uploadFileToServer(file) {
  const data = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const { data: resp } = await api.post("/admin/upload-image", { data, name: file.name });
  return resp.url;
}

export function ImageUpload({ value, onChange, testid }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const tid = testid || "image-upload";
  const pick = () => inputRef.current?.click();
  const handle = async (f) => {
    if (!f) return;
    if (!isAllowedImage(f)) return toast.error("Please choose a HEIC, JPG or PNG image");
    if (f.size > 8_000_000) return toast.error("Image too large (max 8MB)");
    setBusy(true);
    try { const url = await uploadFileToServer(f); onChange(url); toast.success("Image uploaded"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const onFile = async (e) => { await handle(e.target.files?.[0]); e.target.value = ""; };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); };
  return (
    <div className={`flex items-center gap-3 rounded-2xl border-2 border-dashed p-2 transition-colors ${drag ? "border-terracotta bg-terracotta/5" : "border-transparent"}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop} data-testid={tid}>
      {value ? (
        <div className="relative shrink-0">
          <img src={toAbsoluteUrl(value)} alt="" className="w-16 h-16 rounded-xl object-cover border border-cream-alt"/>
          <button type="button" data-testid={`${tid}-clear`} onClick={() => onChange("")} aria-label="Remove image" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-charcoal text-white grid place-items-center"><X size={11}/></button>
        </div>
      ) : (
        <div className="w-16 h-16 shrink-0 rounded-xl bg-cream-alt grid place-items-center text-charcoal-muted"><Image size={20}/></div>
      )}
      <div className="flex-1 space-y-1 min-w-0">
        <input data-testid={`${tid}-url`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL, upload, or drag & drop…" className="w-full text-xs rounded-xl border border-cream-alt px-3 py-2"/>
        <div className="flex gap-2 items-center">
          <button type="button" data-testid={`${tid}-btn`} onClick={pick} disabled={busy} className="text-xs rounded-full bg-terracotta text-white px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"><Upload size={12}/> {busy ? "Uploading…" : value ? "Replace" : "Upload Image"}</button>
          <span className="text-[10px] text-charcoal-muted">HEIC, JPG, PNG · drag & drop</span>
        </div>
      </div>
      <input ref={inputRef} data-testid={`${tid}-file`} type="file" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif,.jpg,.jpeg,.png" className="hidden" onChange={onFile}/>
    </div>
  );
}
